import { type SqliteAsyncConnection } from '../../../../shared/localDb';
import { getCatalogRamSnapshotCache } from '../../../../catalog/infra/catalogRamSnapshotCache';
import type { RemoteSnapshotStockRow } from '../sync/remoteSnapshotStockApply';
export type StockAdjustmentRow = {
  id: string;
  product_id: string;
  branch_id: string;
  quantity: number;
  avg_cost: number;
  variant_id?: string | null;
};

export type ExistingStockReceiveRow = {
  id: string;
  quantity: number;
  avg_cost: number;
  min_quantity: number;
  expires_at: string | null;
};

export type BulkCoefficientStockRow = {
  id: string;
  product_id: string;
  quantity: number;
  avg_cost: number;
  unit_of_measure: string | null;
};

export async function getStockRowForAdjustment(stockId: string): Promise<StockAdjustmentRow | null> {
  const stocks = (getCatalogRamSnapshotCache()?.stocks ?? []) as RemoteSnapshotStockRow[];
        const row =
          stocks.find((s) => String(s.id ?? '') === stockId) ??
          stocks.find((s) => `${s.product_id}:${s.branch_id}` === stockId);
        if (!row) return null;
        return {
          id: String(row.id ?? `${row.product_id}:${row.branch_id}`),
          product_id: String(row.product_id),
          branch_id: String(row.branch_id),
          quantity: Number(row.quantity ?? 0),
          avg_cost: Number(row.avg_cost ?? 0),
          variant_id: (row as { variant_id?: string | null }).variant_id ?? null,
        };
}

export async function getFreshStockQuantity(
  _conn: SqliteAsyncConnection,
  stockId: string
): Promise<number | null> {
  // ADR-017 Fase 4: SQLite local eliminado — leer de catalogRamSnapshotCache.
  const row = (getCatalogRamSnapshotCache()?.stocks ?? []).find(
    (s) => String(s.id ?? '') === stockId || `${s.product_id}:${s.branch_id}` === stockId
  );
  return row ? Number(row.quantity ?? 0) : null;
}

export async function getFreshStockQuantityAndCost(
  _conn: SqliteAsyncConnection,
  stockId: string
): Promise<{ quantity: number; avg_cost: number } | null> {
  // ADR-017 Fase 4: SQLite local eliminado — leer de catalogRamSnapshotCache.
  const row = (getCatalogRamSnapshotCache()?.stocks ?? []).find(
    (s) => String(s.id ?? '') === stockId || `${s.product_id}:${s.branch_id}` === stockId
  );
  if (!row) return null;
  return { quantity: Number(row.quantity ?? 0), avg_cost: Number(row.avg_cost ?? 0) };
}

export async function getExistingStockForReceive(
  _conn: SqliteAsyncConnection,
  productId: string,
  branchId: string,
  variantId?: string | null,
): Promise<ExistingStockReceiveRow | null> {
  // ADR-017 Fase 4: SQLite local eliminado — delega en findStockInWebCache (RAM cache).
  return findStockInWebCache(productId, branchId, variantId);
}

export function findStockInWebCache(
  productId: string,
  branchId: string,
  variantId?: string | null,
): ExistingStockReceiveRow | null {
  const row = (getCatalogRamSnapshotCache()?.stocks ?? []).find((s) => {
    if (s.product_id !== productId || s.branch_id !== branchId) return false;
    const rowVariantId = (s as { variant_id?: string | null }).variant_id ?? null;
    if (variantId) return rowVariantId === variantId;
    return rowVariantId == null || rowVariantId === '';
  });
  if (!row) return null;
  return {
    id: String(row.id ?? crypto.randomUUID()),
    quantity: Number(row.quantity ?? 0),
    avg_cost: Number(row.avg_cost ?? 0),
    min_quantity: Number(row.min_quantity ?? 0),
    expires_at: null,
  };
}

export async function getLotRemainingForSync(
  _conn: SqliteAsyncConnection,
  lotId: string
): Promise<{ remaining_quantity: number; depleted_at: string | null } | null> {
  // ADR-017 Fase 4: SQLite local eliminado — leer stockLots de catalogRamSnapshotCache.
  const lot = (getCatalogRamSnapshotCache()?.stockLots ?? []).find((l) => l.id === lotId);
  if (!lot) return null;
  return { remaining_quantity: lot.remaining_quantity, depleted_at: lot.depleted_at };
}

export async function listStocksForBulkCoefficient(
  branchId: string,
  productIds: string[]
): Promise<BulkCoefficientStockRow[]> {
  if (productIds.length === 0) return [];

  const cache = getCatalogRamSnapshotCache();
        const products = cache?.products ?? [];
        const unitByProduct = new Map(
          products.map((p) => [String(p.id), p.unit_of_measure ?? 'unit']),
        );
        const idSet = new Set(productIds);
        return (cache?.stocks ?? [])
          .filter(
            (s) =>
              s.branch_id === branchId &&
              idSet.has(String(s.product_id)) &&
              !(s as { variant_id?: string | null }).variant_id,
          )
          .map((s) => ({
            id: String(s.id ?? `${s.product_id}:${s.branch_id}`),
            product_id: String(s.product_id),
            quantity: Number(s.quantity ?? 0),
            avg_cost: Number(s.avg_cost ?? 0),
            unit_of_measure: unitByProduct.get(String(s.product_id)) ?? 'unit',
          }));
}
